import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import { useScreenCapture } from '../contexts/ScreenCaptureContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import appLogo from '../assets/logo.svg';
import { getInitials } from '../lib/utils';
import AnnouncementBanner from './AnnouncementBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Clock, 
  LayoutDashboard, 
  FolderKanban, 
  Calendar, 
  BarChart3, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  Play,
  Square,
  Pause,
  ChevronDown,
  ChevronRight,
  Timer,
  Sparkles,
  LifeBuoy,
  Palmtree,
  ClipboardCheck,
  Building2,
  UsersRound,
  Bell,
  User,
  Users2,
  Camera,
  AlertTriangle,
  Coffee,
  Bot,
  CreditCard,
  CalendarDays,
  ArrowUpCircle,
  FileText,
  Monitor,
  Apple,
  Activity,
  Briefcase,
  CalendarClock,
  Download,
  MapPin,
  DollarSign,
  Link2
} from 'lucide-react';
import { cn } from '../lib/utils';
import NotificationBell from './NotificationBell';
import AdminNotificationBell from './AdminNotificationBell';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useSubscription } from './SubscriptionGuard';
import { useFeatures } from '../contexts/FeatureContext';

// Map feature flag keys to nav paths that should be hidden when disabled
const FEATURE_PATH_MAP = {
  payroll: ['/admin/payroll', '/my-payslips', '/admin/tds-reports'],
  invoicing: ['/invoices'],
  screenshots: ['/team-screenshots'],
  location_tracking: ['/team-locations'],
  usage_tracking: ['/usage', '/admin/usage-categories'],
  ai_assistant: ['/ai-assistant'],
  time_off: ['/time-off', '/admin/time-off', '/holidays'],
  timesheets: ['/timesheet', '/team-timesheet', '/admin/approvals'],
};

// Build a set of hidden paths from disabled features
function getHiddenPaths(features) {
  const hidden = new Set();
  for (const [key, paths] of Object.entries(FEATURE_PATH_MAP)) {
    if (features[key] === false) {
      paths.forEach(p => hidden.add(p));
    }
  }
  return hidden;
}

// Check if a nav group should be completely hidden
const FEATURE_GROUP_MAP = {
  'ai-assistant': 'ai_assistant',
};

// Paths hidden on the free individual plan
const freeHiddenPaths = new Set([
  '/time-off', '/holidays', '/team',
  '/admin/notifications', '/admin/approvals', '/admin/time-off',
  '/admin/reports', '/billing', '/invoices', '/admin/payroll',
]);

// Free individual plan nav - personal tools + essentials
const freeNavItemsDef = [
  { icon: LayoutDashboard, labelKey: 'nav.dashboard', path: '/dashboard' },
  { icon: Timer, labelKey: 'nav.timeTracker', path: '/tracker' },
  { icon: FolderKanban, labelKey: 'nav.projects', path: '/admin/projects' },
  { icon: Building2, labelKey: 'nav.clients', path: '/admin/clients' },
  { icon: Calendar, labelKey: 'nav.calendar', path: '/calendar' },
  { icon: Clock, labelKey: 'nav.timesheet', path: '/timesheet' },
  { icon: UsersRound, labelKey: 'nav.people', path: '/admin/people' },
  { icon: Building2, labelKey: 'nav.workspaceSettings', path: '/admin/workspace-settings' },
];

// ─── Grouped Navigation Structure ───
// Each group has: id, label, icon, items[], roles[] (which roles see the group)
// Each item has: icon, labelKey, path, roles[] (optional, defaults to group roles)

const navGroups = {
  // Employee / Manager personal mode groups
  personal: [
    {
      id: 'reports-dashboard',
      label: 'Reports & Dashboard',
      icon: LayoutDashboard,
      items: [
        { icon: LayoutDashboard, labelKey: 'nav.dashboard', path: '/dashboard' },
        { icon: Timer, labelKey: 'nav.timeTracker', path: '/tracker' },
        { icon: Calendar, labelKey: 'nav.calendar', path: '/calendar' },
      ]
    },
    {
      id: 'my-time',
      label: 'My Time',
      icon: CalendarClock,
      items: [
        { icon: Clock, labelKey: 'nav.timesheet', path: '/timesheet' },
        { icon: Palmtree, labelKey: 'nav.timeOff', path: '/time-off' },
        { icon: DollarSign, labelKey: 'nav.myPayslips', path: '/my-payslips' },
        { icon: CalendarDays, labelKey: 'nav.holidays', path: '/holidays' },
      ]
    },
  ],
  // Admin / Manager team mode groups
  admin: [
    {
      id: 'ai-assistant',
      label: 'AI Assistant',
      icon: Bot,
      isDirectLink: true,
      path: '/ai-assistant',
      roles: ['admin', 'manager'],
      isNew: true,
    },
    {
      id: 'contact-support',
      label: 'Contact Support',
      icon: LifeBuoy,
      isDirectLink: true,
      path: '/contact-support',
      roles: ['admin', 'manager'],
    },
    {
      id: 'reports-dashboard',
      label: 'Reports & Dashboard',
      icon: LayoutDashboard,
      items: [
        { icon: LayoutDashboard, labelKey: 'nav.dashboard', path: '/admin/dashboard', roles: ['admin', 'manager'] },
        { icon: BarChart3, labelKey: 'nav.reports', path: '/admin/reports', roles: ['admin', 'manager'] },
      ]
    },
    {
      id: 'manage',
      label: 'Manage',
      icon: Briefcase,
      items: [
        { icon: FolderKanban, labelKey: 'nav.projects', path: '/admin/projects', roles: ['admin', 'manager'] },
        { icon: Building2, labelKey: 'nav.clients', path: '/admin/clients', roles: ['admin', 'manager'] },
        { icon: UsersRound, labelKey: 'nav.people', path: '/admin/people', roles: ['admin', 'manager'] },
        { icon: Clock, labelKey: 'nav.teamTimesheet', path: '/team-timesheet', roles: ['admin', 'manager'] },
      ]
    },
    {
      id: 'time-leave',
      label: 'Time & Leave',
      icon: CalendarClock,
      items: [
        { icon: ClipboardCheck, labelKey: 'nav.timeApprovals', path: '/admin/approvals', roles: ['admin', 'manager'] },
        { icon: Palmtree, labelKey: 'nav.timeOff', path: '/admin/time-off', roles: ['admin', 'manager'] },
        { icon: CalendarDays, labelKey: 'nav.holidays', path: '/holidays', roles: ['admin', 'manager'] },
      ]
    },
    {
      id: 'activity',
      label: 'Activity',
      icon: Monitor,
      items: [
        { icon: Monitor, labelKey: 'nav.usageTracking', path: '/usage', roles: ['admin', 'manager'], isNew: true },
        { icon: Activity, label: 'Activity Monitor', path: '/activity-monitor', roles: ['admin', 'manager'], isNew: true },
        { icon: Camera, labelKey: 'nav.teamScreenshots', path: '/team-screenshots', roles: ['admin', 'manager'] },
        { icon: MapPin, labelKey: 'nav.teamLocations', path: '/team-locations', roles: ['admin', 'manager'], isNew: true },
        { icon: Settings, labelKey: 'nav.usageCategories', path: '/admin/usage-categories', roles: ['admin'] },
      ]
    },
    {
      id: 'finance-payroll',
      label: 'Finance & Payroll',
      icon: DollarSign,
      items: [
        { icon: FileText, labelKey: 'nav.invoices', path: '/invoices', roles: ['admin', 'manager'] },
        { icon: DollarSign, labelKey: 'nav.payroll', path: '/admin/payroll', roles: ['admin'] },
        { icon: FileText, label: 'TDS Reports', path: '/admin/tds-reports', roles: ['admin'] },
      ]
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      items: [
        { icon: Building2, labelKey: 'nav.workspaceSettings', path: '/admin/workspace-settings', roles: ['admin'] },
        { icon: Link2, label: 'Integrations', path: '/admin/integrations', roles: ['admin'] },
        { icon: CreditCard, labelKey: 'nav.billing', path: '/billing', roles: ['admin'] },
        { icon: Bell, labelKey: 'nav.notifications', path: '/admin/notifications', roles: ['admin'] },
      ]
    },
  ]
};

// ─── Collapsible NavGroup Component ───
const NavGroup = ({ id, label, icon: GroupIcon, items, location, t, setSidebarOpen, colorClass, collapsedGroups, toggleGroup }) => {
  const isCollapsed = collapsedGroups[id] || false;
  const isOpen = !isCollapsed;
  const hasActiveChild = items.some(item => location.pathname === item.path);

  return (
    <div className="mt-0.5" data-testid={`nav-group-${id}`}>
      {/* Group Header */}
      <button
        onClick={() => toggleGroup(id)}
        className={cn(
          "w-full flex items-center gap-2.5 px-3 py-2 group cursor-pointer rounded-lg transition-all duration-200 relative",
          isOpen
            ? "text-slate-900 dark:text-white bg-blue-100 dark:bg-blue-900/40"
            : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50/60 dark:hover:bg-slate-800/30"
        )}
        data-testid={`nav-group-toggle-${id}`}
      >
        {/* Left accent bar for expanded state */}
        {isOpen && (
          <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-gradient-to-b from-blue-500 to-indigo-500" />
        )}
        {GroupIcon && (
          <GroupIcon className={cn(
            "w-[18px] h-[18px] flex-shrink-0 transition-colors",
            isOpen || hasActiveChild ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300"
          )} />
        )}
        <span className={cn(
          "text-[13px] font-semibold flex-1 text-left transition-colors tracking-normal"
        )}>
          {label}
        </span>
        <ChevronDown className={cn(
          "w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200",
          isOpen ? "text-blue-500 rotate-0" : "text-slate-400 dark:text-slate-500 -rotate-90",
        )} />
      </button>

      {/* Group Items (sub-tabs) */}
      <div className={cn(
        "overflow-hidden transition-all duration-200 ease-out",
        isCollapsed ? "max-h-0 opacity-0" : "max-h-[500px] opacity-100"
      )}>
        <div className="ml-3 pl-2 mt-0.5 mb-1 border-l-[2px] border-slate-200/70 dark:border-slate-700/50 space-y-[1px]">
          {items.map((item) => {
            const isActive = location.pathname === item.path;
            const ItemIcon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-2.5 pl-2.5 pr-3 py-[7px] rounded-lg text-[13px] transition-all duration-150 relative group/item",
                  isActive
                    ? "text-blue-600 dark:text-blue-400 bg-blue-50/80 dark:bg-blue-500/10 font-medium"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 font-normal"
                )}
                data-testid={`nav-${item.labelKey ? item.labelKey.split('.').pop().toLowerCase().replace(/([A-Z])/g, '-$1') : item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <ItemIcon className={cn(
                  "w-4 h-4 flex-shrink-0 transition-colors",
                  isActive
                    ? "text-blue-500 dark:text-blue-400"
                    : "text-slate-400 dark:text-slate-500 group-hover/item:text-slate-500 dark:group-hover/item:text-slate-400"
                )} />
                <span>{item.label || t(item.labelKey)}</span>
                {item.isNew && (
                  <span className="ml-auto text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                    New
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { isRunning, isPaused, elapsedTime, formatTime, stopTimer, pauseTimer, resumeTimer, resetTimer, showIdleDialog, idleMinutes, keepIdleTime, discardIdleTime } = useTimer();
  const { isEnabled: screenCaptureEnabled, hasPermission: screenCapturePermission, captureCount } = useScreenCapture();
  const location = useLocation();
  const navigate = useNavigate();
  const { subscription, loading: subscriptionLoading } = useSubscription() || {};
  const isFreePlan = subscription?.plan === 'free';
  const { features } = useFeatures();
  const featureHiddenPaths = getHiddenPaths(features);

  // Collapsible nav groups — persist state in localStorage
  // Default: all groups collapsed. Users manually expand as needed.
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const version = localStorage.getItem('navCollapsedVersion');
      if (version === '3') {
        const saved = localStorage.getItem('navCollapsedGroups');
        if (saved) return JSON.parse(saved);
      }
    } catch {}
    // First visit or version upgrade: collapse all groups by default
    const allCollapsed = {};
    [...navGroups.admin, ...navGroups.personal].forEach(g => { allCollapsed[g.id] = true; });
    localStorage.setItem('navCollapsedGroups', JSON.stringify(allCollapsed));
    localStorage.setItem('navCollapsedVersion', '3');
    return allCollapsed;
  });

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => {
      const wasCollapsed = prev[groupId];
      if (wasCollapsed) {
        // Opening this group — collapse all others (accordion)
        const next = {};
        Object.keys(prev).forEach(key => { next[key] = key !== groupId; });
        localStorage.setItem('navCollapsedGroups', JSON.stringify(next));
        return next;
      } else {
        // Closing this group
        const next = { ...prev, [groupId]: true };
        localStorage.setItem('navCollapsedGroups', JSON.stringify(next));
        return next;
      }
    });
  };

  // Manager view mode: 'team' or 'personal'
  const [managerViewMode, setManagerViewMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('managerViewMode') || 'team';
    }
    return 'team';
  });

  // Persist manager view mode
  useEffect(() => {
    if (user?.role === 'manager') {
      localStorage.setItem('managerViewMode', managerViewMode);
    }
  }, [managerViewMode, user?.role]);

  // Sync manager view mode with current route on navigation
  useEffect(() => {
    if (user?.role === 'manager') {
      // If on admin dashboard, ensure view mode is 'team'
      if (location.pathname.startsWith('/admin') || location.pathname === '/usage' || location.pathname === '/team-screenshots' || location.pathname === '/team-locations') {
        if (managerViewMode !== 'team') {
          setManagerViewMode('team');
        }
      }
      // If on personal dashboard/tracker/timesheet, ensure view mode is 'personal'
      else if (['/dashboard', '/tracker', '/timesheet', '/time-off', '/reports', '/settings', '/calendar'].includes(location.pathname)) {
        if (managerViewMode !== 'personal') {
          setManagerViewMode('personal');
        }
      }
    }
  }, [location.pathname, user?.role]);

  // Toggle manager view mode
  const toggleManagerViewMode = () => {
    const newMode = managerViewMode === 'team' ? 'personal' : 'team';
    setManagerViewMode(newMode);
    // Navigate to appropriate dashboard
    if (newMode === 'personal') {
      navigate('/dashboard');
    } else {
      navigate('/admin/dashboard');
    }
  };

  const handleLogout = () => {
    // Reset timer state before logout
    resetTimer();
    logout();
    navigate('/login');
  };

  const handlePauseResume = () => {
    if (isPaused) {
      resumeTimer();
    } else {
      pauseTimer();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Announcement Banner */}
      <AnnouncementBanner />
      {/* Subtle background pattern */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTAgMGg0MHY0MEgweiIvPjwvZz48L2c+PC9zdmc+')] opacity-50 pointer-events-none" />

      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-16 glass-header z-50">
        <div className="flex items-center justify-between h-full px-4 lg:px-6 max-w-[2000px] mx-auto">
          {/* Left */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            <Link to="/dashboard" className="flex items-center gap-3 group">
              <div className="relative">
                <img src={appLogo} alt="Inspiro Tracker" className="w-12 h-12 rounded-xl object-contain" />
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Inspiro Tracker
                </span>
                <span className="block text-[10px] font-medium text-slate-400 -mt-0.5">
                  Time Management
                </span>
              </div>
            </Link>
          </div>

          {/* Center - Running Timer */}
          {isRunning && (
            <div className={cn(
              "hidden md:flex items-center gap-3 px-4 py-2 rounded-2xl border transition-all",
              isPaused 
                ? "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-500/20"
                : "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100 dark:from-blue-500/10 dark:to-indigo-500/10 dark:border-blue-500/20"
            )}>
              <div className="relative">
                <div className={cn(
                  "w-2.5 h-2.5 rounded-full",
                  isPaused ? "bg-amber-500" : "bg-red-500"
                )} />
                {!isPaused && (
                  <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-red-500 animate-ping opacity-75" />
                )}
              </div>
              <span className={cn(
                "timer-display text-lg font-bold",
                isPaused ? "text-amber-600 dark:text-amber-400" : "text-blue-600 dark:text-blue-400"
              )} data-testid="header-timer-display">
                {formatTime(elapsedTime)}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handlePauseResume}
                  className={cn(
                    "h-8 w-8 p-0 rounded-xl",
                    isPaused 
                      ? "hover:bg-amber-100 dark:hover:bg-amber-900/30"
                      : "hover:bg-blue-100 dark:hover:bg-blue-900/30"
                  )}
                  data-testid="header-pause-timer-btn"
                >
                  {isPaused ? (
                    <Play className="w-4 h-4 text-amber-600" />
                  ) : (
                    <Pause className="w-4 h-4 text-blue-600" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={stopTimer}
                  className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl group"
                  data-testid="header-stop-timer-btn"
                >
                  <Square className="w-4 h-4 text-slate-400 group-hover:text-red-500 transition-colors" />
                </Button>
              </div>
              {/* Screen Capture Indicator */}
              {screenCaptureEnabled && screenCapturePermission && (
                <div 
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
                  title={`Screen capture active - ${captureCount} screenshots`}
                  data-testid="header-capture-indicator"
                >
                  <Camera className="w-3.5 h-3.5" />
                  <span>{captureCount}</span>
                </div>
              )}
            </div>
          )}

          {/* Right */}
          <div className="flex items-center gap-2">
            {/* Language Switcher - Disabled until multi-language support is ready */}
            {/* <LanguageSwitcher /> */}
            
            {/* Notification Bell - Admin users see AdminNotificationBell, others see regular NotificationBell */}
            {user?.role === 'admin' ? (
              <AdminNotificationBell />
            ) : (
              <NotificationBell />
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2.5 px-2 h-10 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8 ring-2 ring-white dark:ring-slate-800 shadow-sm">
                    {user?.profile_photo && (
                      <AvatarImage src={user.profile_photo} alt={user.name} />
                    )}
                    <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-xs font-semibold">
                      {user ? getInitials(user.name) : 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <span className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {user?.name}
                    </span>
                    <span className={cn(
                      "inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded",
                      user?.role === 'admin' && "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300",
                      user?.role === 'manager' && managerViewMode === 'personal' && "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300",
                      user?.role === 'manager' && managerViewMode === 'team' && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300",
                      user?.role === 'employee' && "bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300"
                    )}>
                      {user?.role === 'manager' && managerViewMode === 'personal' ? 'My Time' : user?.role}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 rounded-xl p-2">
                <DropdownMenuLabel className="px-2">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 flex-shrink-0">
                      {user?.profile_photo && (
                        <AvatarImage src={user.profile_photo} alt={user.name} />
                      )}
                      <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-semibold">
                        {user ? getInitials(user.name) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{user?.name}</p>
                      <p className="text-xs font-normal text-slate-500 truncate" title={user?.email}>{user?.email}</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-2" />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer rounded-lg" data-testid="settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('nav.settings')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-2" />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/20 rounded-lg" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar - Fixed 240px width */}
      <aside className={cn(
        "fixed top-16 bottom-0 w-60 bg-white dark:bg-slate-900/95 backdrop-blur-xl z-40",
        "transform transition-transform duration-300 ease-out",
        isRTL ? "right-0 border-l border-slate-200/80 dark:border-slate-800/80" : "left-0 border-r border-slate-200/80 dark:border-slate-800/80",
        sidebarOpen
          ? "translate-x-0"
          : isRTL
            ? "translate-x-full lg:translate-x-0"
            : "-translate-x-full lg:translate-x-0"
      )}>
        <nav className="p-3 space-y-0.5 overflow-y-auto h-full pb-8" data-testid="sidebar">
          {/* Show skeleton while subscription is loading to prevent nav flash */}
          {subscriptionLoading ? (
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-10 rounded-xl bg-slate-100 dark:bg-slate-800/60 animate-pulse" />
              ))}
            </div>
          ) : (
          <>
          {/* Free Individual Plan Navigation */}
          {isFreePlan && user?.role === 'admin' && (
            <>
              {freeNavItemsDef.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={cn("sidebar-link", isActive && "active")}
                    data-testid={`nav-${item.labelKey.split('.').pop().toLowerCase().replace(/([A-Z])/g, '-$1')}`}
                  >
                    <item.icon className="w-[18px] h-[18px]" />
                    <span>{t(item.labelKey)}</span>
                  </Link>
                );
              })}
              <Link
                to="/upgrade"
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "sidebar-link",
                  "text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20",
                  location.pathname === '/upgrade' && "active bg-amber-50 dark:bg-amber-900/20"
                )}
                data-testid="nav-upgrade"
              >
                <ArrowUpCircle className="w-[18px] h-[18px]" />
                <span>Upgrade</span>
              </Link>
            </>
          )}

          {/* Grouped Navigation for paid plans */}
          {!isFreePlan && (() => {
            // Determine which groups to show
            const isAdminView = user?.role === 'admin' || (user?.role === 'manager' && managerViewMode === 'team');
            const groups = isAdminView ? navGroups.admin : navGroups.personal;
            const colorClass = user?.role === 'admin'
              ? { text: 'text-purple-700 dark:text-purple-300', active: 'bg-purple-50 dark:bg-purple-900/20', hover: 'hover:bg-purple-50/50 dark:hover:bg-purple-900/10' }
              : user?.role === 'manager' && managerViewMode === 'team'
                ? { text: 'text-emerald-700 dark:text-emerald-300', active: 'bg-emerald-50 dark:bg-emerald-900/20', hover: 'hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10' }
                : null;

            return groups.map((group) => {
              // Direct link item (e.g. AI Assistant)
              if (group.isDirectLink) {
                if (group.roles && !group.roles.includes(user?.role)) return null;
                // Check feature flag for direct link groups
                const featureKey = FEATURE_GROUP_MAP[group.id];
                if (featureKey && features[featureKey] === false) return null;
                if (featureHiddenPaths.has(group.path)) return null;
                const isActive = location.pathname === group.path;
                const GroupIcon = group.icon;
                return (
                  <div key={group.id} className="mt-0.5" data-testid={`nav-group-${group.id}`}>
                    <Link
                      to={group.path}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200 relative",
                        isActive
                          ? "text-blue-600 dark:text-blue-400 bg-blue-50/80 dark:bg-blue-500/10 font-semibold"
                          : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50/60 dark:hover:bg-slate-800/30 font-semibold"
                      )}
                      data-testid={`nav-${group.id}`}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-gradient-to-b from-blue-500 to-indigo-500" />
                      )}
                      {GroupIcon && <GroupIcon className={cn("w-[18px] h-[18px]", isActive ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-slate-500")} />}
                      <span className="text-[13px]">{group.label}</span>
                      {group.isNew && (
                        <span className="ml-auto text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                          New
                        </span>
                      )}
                    </Link>
                  </div>
                );
              }

              // Filter items by role and feature flags
              const visibleItems = group.items.filter(item => {
                if (item.roles && !item.roles.includes(user?.role)) return false;
                if (featureHiddenPaths.has(item.path)) return false;
                return true;
              });
              if (visibleItems.length === 0) return null;

              return (
                <NavGroup
                  key={group.id}
                  id={group.id}
                  label={group.label}
                  icon={group.icon}
                  items={visibleItems}
                  location={location}
                  t={t}
                  setSidebarOpen={setSidebarOpen}
                  colorClass={isAdminView ? colorClass : null}
                  collapsedGroups={collapsedGroups}
                  toggleGroup={toggleGroup}
                />
              );
            });
          })()}
          
          {/* Manager View Mode Toggle */}
          {user?.role === 'manager' && (
            <div className="pt-4 pb-2">
              <div className="flex items-center gap-2 px-3 mb-3">
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t('nav.personalView').split(' ')[0]}</span>
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
              </div>
              
              {/* Pill Toggle */}
              <div 
                className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-xl p-1 mx-1"
                data-testid="manager-view-toggle"
              >
                <button
                  onClick={() => {
                    if (managerViewMode !== 'team') {
                      setManagerViewMode('team');
                      navigate('/admin/dashboard');
                    }
                  }}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-medium transition-all",
                    managerViewMode === 'team'
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 shadow-sm"
                      : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                  )}
                  data-testid="toggle-team-view"
                >
                  <Users2 className="w-4 h-4" />
                  <span>{t('nav.teamView')}</span>
                </button>
                <button
                  onClick={() => {
                    if (managerViewMode !== 'personal') {
                      setManagerViewMode('personal');
                      navigate('/dashboard');
                    }
                  }}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-medium transition-all",
                    managerViewMode === 'personal'
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 shadow-sm"
                      : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                  )}
                  data-testid="toggle-my-time"
                >
                  <User className="w-4 h-4" />
                  <span>{t('nav.personalView')}</span>
                </button>
              </div>
            </div>
          )}
          </>
          )}

          {/* Downloads Section */}
          <div className="pt-3 mt-3 border-t border-slate-100 dark:border-slate-800/60">
            <div className="px-3 mb-1.5">
              <span className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 tracking-wide">Downloads</span>
            </div>
            <a
              href={`${process.env.REACT_APP_DOWNLOAD_BASE_URL || 'https://api.inspiroworks.com/downloads'}/InspiroTracker.exe`}
              className="flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-all duration-150"
              data-testid="sidebar-download-windows"
            >
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/></svg>
              <span>Windows</span>
            </a>
            <a
              href={`${process.env.REACT_APP_DOWNLOAD_BASE_URL || 'https://api.inspiroworks.com/downloads'}/InspiroTracker.dmg`}
              className="flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-all duration-150"
              data-testid="sidebar-download-mac"
            >
              <Apple className="w-4 h-4 flex-shrink-0" />
              <span>macOS</span>
            </a>
          </div>
        </nav>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className={cn("pt-16 min-h-screen relative", isRTL ? "lg:pr-60" : "lg:pl-60")}>
        <div className="p-6 lg:p-8 max-w-[1600px] mx-auto animate-fade-in" style={{ minHeight: 'calc(100vh - 4rem - 49px)' }}>
          {children}
        </div>
        <footer className="px-6 lg:px-8 py-4 border-t border-slate-100 dark:border-slate-800" data-testid="app-footer">
          <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-400">
            <span>&copy; {new Date().getFullYear()} INPW Technologies Pvt Ltd. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="hover:text-slate-600 dark:hover:text-slate-300 transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-slate-600 dark:hover:text-slate-300 transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </footer>
      </main>

      {/* Idle Detection Dialog - Shows when laptop wakes from sleep */}
      <Dialog open={showIdleDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="idle-dialog">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center mb-2">
              <Coffee className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-center text-xl">{t('common.appName')}</DialogTitle>
            <DialogDescription className="text-center">
              We detected approximately <span className="font-semibold text-slate-700 dark:text-slate-300">{idleMinutes} minutes</span> of inactivity 
              (your computer may have been asleep).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
              What would you like to do with this time?
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => discardIdleTime(idleMinutes)}
              className="flex-1 rounded-xl h-11"
              data-testid="discard-idle-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
              Discard idle time
            </Button>
            <Button 
              onClick={keepIdleTime}
              className="flex-1 btn-premium h-11"
              data-testid="keep-idle-btn"
            >
              <Clock className="w-4 h-4 mr-2" />
              Keep all time
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
